import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import PasswordInput from "../components/PasswordInput";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User as UserIcon, Mail, Phone, KeyRound, Lock } from "lucide-react";

function formatCpf(v) {
  if (!v) return "—";
  const d = String(v).replace(/\D/g, "").padStart(11, "0");
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ email: user?.email || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changing, setChanging] = useState(false);

  if (!user) return null;

  const saveContact = async (e) => {
    e.preventDefault();
    const payload = {};
    if (form.email.trim().toLowerCase() !== user.email) payload.email = form.email.trim();
    if (form.phone.trim() !== user.phone) payload.phone = form.phone.trim();
    if (Object.keys(payload).length === 0) return toast.info("Nada foi alterado");
    setSaving(true);
    try {
      await api.patch("/auth/profile", payload);
      await refresh();
      toast.success("Dados atualizados");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) return toast.error("Nova senha muito curta (mín. 6)");
    if (pwd.new_password !== pwd.confirm) return toast.error("As senhas não coincidem");
    setChanging(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Senha alterada com sucesso");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Meu perfil</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">
          Dados <span className="gold-text">da conta</span>
        </h1>

        <Card className="p-6 mt-6 gold-border" data-testid="profile-readonly-card">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
            Dados de cadastro (somente leitura)
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><UserIcon className="h-3 w-3" /> Nome completo</div>
              <div className="mt-1 font-medium" data-testid="profile-name">{user.name}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> CPF</div>
              <div className="mt-1 font-mono" data-testid="profile-cpf">{formatCpf(user.cpf)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Plano atual</div>
              <div className="mt-1 font-medium gold-text">{user.plan_label}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Código de indicação</div>
              <div className="mt-1 font-mono text-sm">{user.referral_code}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6 mt-6">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Contato</div>
          <form onSubmit={saveContact} className="space-y-4">
            <div>
              <Label htmlFor="email"><Mail className="h-3 w-3 inline mr-1" /> E-mail</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="profile-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone"><Phone className="h-3 w-3 inline mr-1" /> WhatsApp</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(11) 99999-9999"
                required
                data-testid="profile-phone-input"
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="gold-btn hover:opacity-95"
              data-testid="profile-save-btn"
            >
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </form>
        </Card>

        <Card className="p-6 mt-6">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
            <KeyRound className="h-3 w-3 inline mr-1" /> Alterar senha
          </div>
          <form onSubmit={changePassword} className="space-y-4">
            <PasswordInput
              placeholder="Senha atual"
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              required
              data-testid="profile-pwd-current"
            />
            <PasswordInput
              placeholder="Nova senha (mín. 6 caracteres)"
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              required
              minLength={6}
              data-testid="profile-pwd-new"
            />
            <PasswordInput
              placeholder="Confirmar nova senha"
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              required
              minLength={6}
              data-testid="profile-pwd-confirm"
            />
            <Button
              type="submit"
              disabled={changing}
              className="gold-btn hover:opacity-95"
              data-testid="profile-pwd-submit"
            >
              {changing ? "Salvando..." : "Alterar senha"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
